import { GlobalFacility } from '@/graphql/generated/schemas'

interface FacilityWithFloorIdsI {
  defaultFloorId?: string
  floors?: { edges?: { node?: { id: string } }[] }
}

export const uuid = () =>
  (performance.now().toString(36) + Math.random().toString(36)).replace(
    /\./g,
    ''
  )
export const getMajorMinorVersion = (version: string): string => {
  const isMajorMinorPatchVersion = version?.split('.').length - 1 === 2
  if (isMajorMinorPatchVersion) {
    return version?.substring(0, version?.lastIndexOf('.'))
  }
  return version
}

export const getDefaultOrFirstFloorId = (
  facility: FacilityWithFloorIdsI
): string => facility?.defaultFloorId || facility.floors.edges[0]?.node?.id

// Ensure that all cases where handled in switch-case
// Reference: <https://www.meticulous.ai/blog/safer-exhaustive-switch-statements-in-typescript>
export const exhaustiveCheck = (switchArg: never) => {
  const exhaustiveCheck: never = switchArg
  throw new Error(`Unexpected type: ${exhaustiveCheck}`)
}

export const isValidTimestamp = (str: string) => !isNaN(Date.parse(str))

/**
 * Shuffles an array using the Fisher-Yates algorithm.
 *
 * @param array - The array to shuffle
 * @returns A new shuffled array
 */
export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

export type GlobalMapProperties = {
  id: string
  shortName: string
  defaultFloorId: string
  lat: number
  lon: number
  activeIncidentCount: number
}

export type MapOfStringToNumber = Record<string, number>

/**
 * Takes a list of Facilities (`globalFacilities`) & a map of Facilities with Incidents (`mapOfFacilitiesToIncidents`)
 * and merges them into one consumable object for usage within map.
 *
 * These are two distinctly different objects as we can load the list of all Facilities once, and then continue to
 * refresh Incidents after the fact w/o as much strain on DB
 *
 * @param globalFacilities
 * @param mapOfFacilitiesToIncidents
 */
export const mergeCountsIntoFacilities = (
  globalFacilities: GlobalFacility[],
  mapOfFacilitiesToIncidents: MapOfStringToNumber
): GlobalMapProperties[] => {
  if (globalFacilities === undefined) {
    return []
  }

  return globalFacilities.map((el: GlobalFacility) => {
    const activeIncidentCount: number = mapOfFacilitiesToIncidents?.[el.id] ?? 0

    const globalMapProperties: GlobalMapProperties = {
      id: el.id,
      shortName: el.shortName,
      defaultFloorId: el.defaultFloorId,
      lat: el.lat,
      lon: el.lon,
      activeIncidentCount: activeIncidentCount,
    }
    return globalMapProperties
  })
}
